import { useEffect, useState } from 'react'
import { Button, SearchableSelect, TextArea } from '../../components/BaseComponents'
import CanonicalModal from '../../components/BaseComponents/CanonicalModal/CanonicalModal'
import Label from '../../components/BaseComponents/Label'
import AdminCreateCompany from '../../components/Admin/CreateCompany/CreateCompany'
import useApi from '../../hooks/useApi'
import { useAppDispatch, useAppSelector } from '../../redux/hooks/reduxHooks'
import { getCompanyList } from '../../redux/actions/getCompanyListActions'
import { selectCompanyList, selectCompanyListStatus } from '../../redux/slices/getCompanyListSlice'
import { FetchStatus } from '../../types/LoadingStates'
import { SelectDropdown } from '../../types/SelectDropdown'
import { getCSRFToken } from '../../lib/utils'

export type ReviewableUser = {
	id: string
	fullName: string
	email: string
}

type ReviewUpgradeRequestModalProps = {
	user: ReviewableUser | null
	onClose: () => void
	onReviewed: () => void
}

function ReviewUpgradeRequestModal({ user, onClose, onReviewed }: ReviewUpgradeRequestModalProps) {
	const api = useApi()
	const dispatch = useAppDispatch()

	const companyListStatus = useAppSelector(selectCompanyListStatus)
	const companyListData = useAppSelector(selectCompanyList)
	const [companyOptions, setCompanyOptions] = useState<Array<SelectDropdown>>([])
	const [companyId, setCompanyId] = useState('')
	const [reason, setReason] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [createOpen, setCreateOpen] = useState(false)

	useEffect(() => {
		dispatch(getCompanyList(api, { limit: 1000 }))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (companyListStatus === FetchStatus.loaded) {
			const fromList = companyListData.map((company) => ({ label: company.name, value: company.id }))
			// Keep organisations created in this modal even if a later list refresh does not include them yet.
			setCompanyOptions((previous) => [
				...fromList,
				...previous.filter((option) => !fromList.some((item) => item.value === option.value)),
			])
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyListStatus])

	const close = () => {
		setCompanyId('')
		setReason('')
		setError(null)
		onClose()
	}

	const submit = async (action: 'approve' | 'deny') => {
		if (!user) {
			return
		}
		setSubmitting(true)
		setError(null)
		try {
			await api.post(`admin/training-users/${user.id}/approve-upgrade`, {
				_csrf: await getCSRFToken(),
				action,
				companyId: action === 'approve' ? companyId : undefined,
				reason: reason.trim() || undefined,
			})
			close()
			onReviewed()
		} catch (_) {
			setError('Failed to submit the review. Please try again.')
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<>
			{/* Hidden (not stacked) while the create modal is open, so only one dialog is ever visible; form state is kept. */}
			<CanonicalModal
				open={user !== null && !createOpen}
				onClose={() => {
					// Hiding for the create modal also fires the after-close callback; that must not discard the review.
					if (!createOpen) close()
				}}
				size='small'
				title='Review upgrade request'
				subtitle={user ? `${user.fullName} (${user.email})` : undefined}
				actions={
					<div className='flex space-x-3'>
						<Button
							type='Primary'
							size='sm'
							text='Approve'
							disabled={submitting || !companyId}
							onClick={() => submit('approve')}
						/>
						<Button
							type='Outlined'
							size='sm'
							text='Deny'
							disabled={submitting}
							onClick={() => submit('deny')}
						/>
					</div>
				}
			>
				<div className='mt-6'>
					<Label className='block'>Assign to an existing organisation</Label>
					<div className='flex items-start space-x-3'>
						<div className='grow'>
							<SearchableSelect
								options={companyOptions}
								value={companyOptions.find((option) => option.value === companyId)}
								onChange={(option) => setCompanyId(option?.value ?? '')}
							/>
						</div>
						<Button
							type='Outlined'
							size='sm'
							text='Create new'
							context='form-field'
							icon='fa-solid fa-plus'
							hasTrailingIcon
							onClick={() => setCreateOpen(true)}
						/>
					</div>
					<div className='text-charcoal-100 text-sm mt-1'>
						The new organisation is selected automatically once it has been created.
					</div>
				</div>

				<div className='mt-6'>
					<Label className='block'>Reason (optional)</Label>
					<TextArea
						rows={2}
						value={reason}
						onChange={(e) => setReason(e.target.value)}
					/>
					<div className='text-charcoal-100 text-sm mt-1'>
						If you deny this request, the reason will be emailed to the user.
					</div>
				</div>

				{error && <div className='text-error-500 text-sm mt-2'>{error}</div>}
			</CanonicalModal>
			{createOpen && (
				<AdminCreateCompany
					open
					onClose={() => setCreateOpen(false)}
					onCreated={(company) => {
						setCompanyOptions((options) =>
							options.some((option) => option.value === company.id)
								? options
								: [...options, { label: company.name, value: company.id }]
						)
						setCompanyId(company.id)
					}}
				/>
			)}
		</>
	)
}

export default ReviewUpgradeRequestModal
